import type { WireCommitment } from '@/api/hooks';
import type { WireBatchItem, WireCoveringTask } from '@/api/hooks';
import type { WireBrief, WireHeldSender } from '@/api/wire';
import { distinct, explainTask, plural, shortDate, dayGap, dayWords } from '@/tasks/explain';
import type { ItemContext, Party } from '@/ui/context';
import { nameFromAddress, type Who } from '@/ui/who';
import { countryName, stateWords } from '@/ui/words';
import { STARTED_BY, clock } from './copy';
import type { Described } from './describe';
import type { WireItem } from './grid';

/**
 * What an item on the day says about itself (ADR-0155): why it is on the
 * day, who brought it, who owns it, who is involved, who knows more and
 * what to do next. One builder over the item the day already holds and what
 * `describe` resolved from the lists beside it, so no row asks for anything
 * of its own. A task is explained by the task's own builder, so the board's
 * card, the task's page and the day say the same thing.
 *
 * Where the item points at something this screen has not loaded (a thread
 * outside the span, a meeting the calendar has not mirrored), it says so
 * rather than describing what it cannot see.
 */

export interface DayExtras {
  /** The reader's own id. */
  meId?: string | null | undefined;
  /** The brief, when the item is one and it has been read (the row already reads it when opened). */
  brief?: WireBrief | null | undefined;
}

const CHANNEL: Record<string, string> = { email: 'email', chat: 'chat', whatsapp: 'WhatsApp', note: 'note' };

const BRIEF_FOR: Record<WireBrief['forKind'], string> = {
  calendar_event: 'a meeting',
  task: 'a task',
  thread: 'a conversation',
  contact: 'a contact',
};

/** Why it is on this day, from who placed it and what they said: a sentence. */
function placement(item: WireItem): string | null {
  if (item.placedBy === 'candidate') return null;
  const said = item.reason?.trim().replace(/[.!?\s]+$/, '');
  const reason = said ? `: ${said}` : '';
  const started = item.placedBy === 'agent' && item.origin ? `, started by ${STARTED_BY[item.origin]}` : '';
  if (item.placedBy === 'human') return `You placed it${reason}.`;
  const by = item.actorName ?? 'An agent';
  return `${by} ${item.kind === 'calendar_event' ? 'booked' : 'put'} it on your day${reason}${started}.`;
}

const sentence = (s: string): string => (/[.!?”]$/.test(s) ? s : `${s}.`);

function absent(item: WireItem, what: string, who: Who, title: string): ItemContext {
  return {
    subject: { kind: what, title },
    summary: `This points at ${what === 'meeting' ? 'a meeting' : `a ${what}`} that this screen has not loaded: it may be outside this span or no longer visible to you.`,
    why: placement(item) ?? 'It is on your day.',
    source: null,
    broughtBy: null,
    owner: who.me,
    next: {
      tip: 'Nothing more can be said from here. If you remember what it was, open its page or ask your twin; otherwise hide it for today.',
      instead: 'It will describe itself again when the list it belongs to is loaded.',
    },
  };
}

export function explainDayItem(item: WireItem, d: Described, who: Who, now: Date = new Date(), extra: DayExtras = {}): ItemContext {
  const placer: Party | null = item.placedBy === 'agent' ? (item.actor ? who.actor(item.actor) : { name: item.actorName ?? 'An agent', kind: 'agent' }) : null;
  const placedWhy = placement(item);
  const pinned: [string, string | null] = ['On your day', item.pinned ? 'Pinned to the top' : null];
  const meId = extra.meId ?? null;

  switch (item.kind) {
    case 'calendar_event': {
      const e = d.event;
      if (!e) return absent(item, 'meeting', who, d.title);
      const starts = new Date(e.startsAt);
      const ends = new Date(e.endsAt);
      const cancelled = e.status === 'cancelled';
      const hold = e.source === 'platform' && e.status === 'tentative';
      const past = ends.getTime() <= now.getTime();
      const live = starts.getTime() <= now.getTime() && !past;
      const soon = !past && !live && starts.getTime() - now.getTime() <= 60 * 60_000;
      const gap = dayGap(e.startsAt, now);
      const others = e.attendees.map((a) => ({ a, p: who.actor(a.address) })).filter((x): x is { a: typeof x.a; p: Party } => x.p !== null && x.p.name !== 'You' && x.a.humanId !== meId);
      const when = e.allDay ? `all day ${dayWords(gap)}` : `${clock(e.startsAt)}–${clock(e.endsAt)} ${dayWords(gap)}`;
      const people = others.length > 0 ? ` with ${plural(others.length, 'other person', 'other people')}` : '';
      let tip: string;
      let instead: string | undefined;
      if (cancelled) {
        tip = 'This meeting was cancelled in the calendar. Nothing is needed from you; hide it from your day if it is in the way.';
      } else if (hold) {
        tip = 'This is a hold your twin placed at your word. Nobody else has been told. Release it from your calendar if you no longer need the time, or send the invitations from there when you are ready.';
        instead = 'A hold never tells anyone; only your own “Send invitations” does.';
      } else if (past) {
        tip = 'The meeting is over. If capture was on, check what you said you would do in the list below and put it in your day; otherwise add any follow-up as a task.';
        instead = 'Nothing is lost by leaving it: the notes are read once and then shredded after thirty days.';
      } else if (live) {
        tip = `It is on now${e.meetUri ? ': join from the Meet link below' : ''}.`;
        instead = 'Afterwards, the capture setting below decides whether the notes are read.';
      } else if (soon) {
        tip = `It starts at ${clock(e.startsAt)}. Press “Prepare me” above to have your twin write a brief from what the platform holds; it lands here and nothing is sent to anyone.`;
        instead = 'If you cannot go, tell the organiser from your calendar.';
      } else {
        tip = `It is ${dayWords(gap)} at ${clock(e.startsAt)}. Press “Prepare me” above if you want a brief ahead of time; nothing is needed before then.`;
        instead = 'If you cannot go, tell the organiser from your calendar.';
      }
      return {
        subject: { kind: 'meeting', title: d.title },
        summary: `${e.title ?? 'A meeting'}: ${when}${people}${e.meetUri ? ', with a Google Meet' : ''}${cancelled ? '. It was cancelled' : hold ? '. It is a hold' : ''}.`,
        need: cancelled ? undefined : past ? 'Any follow-up you owe from it.' : 'Your time, and to be ready for it.',
        why: placedWhy ?? 'It is on your calendar, and the calendar is mirrored into your day.',
        source: hold
          ? { label: 'A hold your twin placed on your calendar', when: shortDate(e.startsAt) }
          : e.source === 'platform'
            ? { label: 'A meeting your twin set up, with invitations sent', when: shortDate(e.startsAt) }
            : { label: 'Your Google calendar, mirrored under your own key', when: shortDate(e.startsAt) },
        broughtBy: e.source === 'platform' ? (placer ? { ...placer } : who.me) : null,
        owner: who.me,
        involved: distinct([placer, ...others.map(({ a, p }) => ({ ...p, role: stateWords(a.response).toLowerCase() }))]).slice(0, 8),
        knows: who.knowers(e.department).slice(0, 3),
        facts: [['Status', stateWords(e.status)], ['Length', `${Math.max(1, Math.round((ends.getTime() - starts.getTime()) / 60_000))} minutes`], pinned],
        next: { tip, ...(instead ? { instead } : {}) },
      };
    }

    case 'task': {
      const t = d.task;
      if (!t) return absent(item, 'task', who, d.title);
      const base = explainTask(t, who, now, { meId });
      return { ...base, why: placedWhy ? `${placedWhy} ${base.why ?? ''}`.trim() : base.why, involved: distinct([...(base.involved ?? []), placer]) };
    }

    case 'reply_needed':
    case 'chase':
    case 'shared_inbox': {
      const t = d.thread;
      if (!t) return absent(item, 'conversation', who, d.title);
      const channel = CHANNEL[t.primaryChannel] ?? 'conversation';
      const lastIsMe = t.lastDirection === 'outbound';
      const last = t.lastFrom ? (lastIsMe ? 'you' : nameFromAddress(t.lastFrom)) : null;
      const since = t.waitingSince ?? t.lastMessageAt;
      const days = since ? Math.max(0, -dayGap(since, now)) : null;
      const claimedMe = Boolean(meId) && t.claimedByHuman === meId;
      const claimedOther = Boolean(t.claimedByHuman) && !claimedMe;
      // The agent that claimed it, by its own handle where the row says (0162).
      const agent = t.claimedByAgent ? ((t.claimedByAgentHandle ? who.actor(t.claimedByAgentHandle) : null) ?? who.agentsOf(t.department)[0] ?? { name: 'An agent', kind: 'agent' as const }) : null;
      const sender = t.lastDirection === 'inbound' && t.lastFrom ? who.actor(t.lastFrom) : null;
      const subject = t.subject ? `“${t.subject}”` : 'with no subject';
      let tip: string;
      let instead: string | undefined;
      if (item.kind === 'shared_inbox') {
        if (claimedMe) {
          tip = 'You claimed this. Reply from the conversation below, or press “Draft it for me” and your twin prepares a draft for you to read and send.';
          instead = 'If you cannot take it after all, release it from the claim control so it returns to everyone’s day.';
        } else if (claimedOther) {
          tip = `${t.claimedByName ?? 'Someone'} is on it and it has left your day. Nothing is needed from you.`;
        } else if (agent) {
          tip = `${agent.name} has taken it and it has left every human day. Nothing is needed from you unless it hands it back.`;
        } else {
          tip = 'It is on every member’s day until someone claims it. If you will answer it, claim it below so it leaves everyone else’s day; otherwise leave it for whoever will.';
          instead = 'If an agent takes it, it leaves every human day by itself.';
        }
      } else if (item.kind === 'chase') {
        tip = `They have not answered${since ? ` since ${shortDate(since)}${days ? ` (${plural(days, 'day')})` : ''}` : ''}. Press “Draft it for me” and your twin prepares a short nudge in Gmail Drafts; you read it and send it.`;
        instead = 'If it no longer matters, hide it for today or postpone it. Nothing is sent by the platform.';
      } else {
        tip = `${days ? `It has waited ${plural(days, 'day')}. ` : ''}Press “Draft it for me” and your twin prepares a reply in Gmail Drafts for you to read and send; nothing goes out without you.`;
        instead = 'Or reply yourself from the conversation below, or postpone it to tomorrow if it can wait.';
      }
      return {
        subject: { kind: 'conversation', title: t.subject ?? d.title },
        summary: `${/^[aeiou]/i.test(channel) ? 'An' : 'A'} ${channel} conversation ${subject}, ${plural(t.messageCount, 'message')}${last ? `, the last from ${last}` : ''}${t.lastMessageAt ? ` on ${shortDate(t.lastMessageAt)}` : ''}.`,
        need:
          item.kind === 'chase'
            ? 'A nudge from you, since you wrote last and they have not answered.'
            : item.kind === 'shared_inbox'
              ? claimedMe
                ? 'Your reply.'
                : claimedOther || agent
                  ? undefined
                  : 'Someone to claim it and answer.'
              : 'A reply from you.',
        why: placedWhy ?? (item.kind === 'chase' ? 'You wrote last and the other side has been quiet.' : item.kind === 'shared_inbox' ? 'It came to a shared inbox you are a member of.' : 'It is filed to you and waits for a reply.'),
        source: { label: t.own ? 'Your own inbox' : t.primaryChannel === 'email' ? 'A message that came in by email' : `A ${channel} conversation`, to: `/mail/${t.id}`, ...(t.lastMessageAt ? { when: shortDate(t.lastMessageAt) } : {}) },
        broughtBy: sender ? { ...sender, ...(t.lastMessageAt ? { when: shortDate(t.lastMessageAt) } : {}) } : null,
        owner: claimedMe ? who.me : claimedOther ? { name: t.claimedByName || 'A colleague', kind: 'person', role: 'claimed it' } : agent ? agent : item.kind === 'shared_inbox' ? null : who.me,
        involved: distinct([agent, placer]),
        knows: who.knowers(t.department).slice(0, 3),
        facts: [['Messages', String(t.messageCount)], ['Waiting since', t.waitingSince ? shortDate(t.waitingSince) : null], pinned],
        next: { tip, ...(instead ? { instead } : {}) },
        open: { to: `/mail/${t.id}`, label: 'Open the conversation' },
      };
    }

    case 'merge_proposal': {
      const m = d.merge;
      if (!m) return absent(item, 'proposal', who, d.title);
      const proposer = who.actor(m.proposedBy);
      const decided = m.state !== 'proposed';
      return {
        subject: { kind: 'proposal', title: d.title },
        summary: `${proposer?.name ?? 'The platform'} noticed two conversations${m.contactName ? ` with ${m.contactName}` : ''} that look like one: “${m.kept.subject ?? 'no subject'}” and “${m.merged.subject ?? 'no subject'}”. ${sentence(m.reason)}`,
        need: decided ? undefined : 'Your decision: merge them, or keep them apart.',
        why: m.mine ? 'The conversations are yours, so the decision is yours; nothing is merged unless you say.' : 'It is on the day of the person who decides it.',
        source: { label: `Noticed by ${proposer?.name ?? 'the platform'}`, when: shortDate(m.createdAt) },
        broughtBy: proposer ? { ...proposer, when: shortDate(m.createdAt) } : null,
        owner: m.mine ? who.me : null,
        knows: who.knowers(m.department).slice(0, 3),
        facts: [['State', decided ? (m.state === 'merged' ? 'Merged' : 'Kept apart') : 'Waiting for a decision'], pinned],
        next: decided
          ? { tip: `Nothing is needed from you. This was ${m.state === 'merged' ? 'merged' : 'kept apart'}.` }
          : {
              tip: 'Read both subjects. Merge only if they are one conversation: every message moves to the older one and both channels keep working.',
              instead: 'If you are not sure, keep them apart; that pair is not proposed again.',
            },
      };
    }

    case 'brief': {
      const b = extra.brief ?? null;
      const prepared = b ? who.actor(b.preparedByHandle ?? b.preparedBy) : placer;
      return {
        subject: { kind: 'brief', title: d.title },
        summary: b
          ? `A brief prepared for ${BRIEF_FOR[b.forKind]}, built from ${plural(b.sources.length, 'source')}. Its words are below.`
          : 'A brief your twin prepared for you. It is no longer readable here, or has not been opened yet.',
        need: 'A read before the thing it is for.',
        why: placedWhy ?? 'It was prepared for you and put on your day.',
        source: b ? { label: `A brief for ${BRIEF_FOR[b.forKind]}`, ...(b.readyAt ? { when: shortDate(b.readyAt) } : {}) } : null,
        broughtBy: prepared ? { ...prepared, ...(b?.readyAt ? { when: shortDate(b.readyAt) } : {}) } : null,
        owner: who.me,
        involved: distinct([prepared]),
        facts: [['Built from', b ? plural(b.sources.length, 'source') : null], ['State', b ? (b.state === 'ready' ? 'Ready' : b.state === 'draft' ? 'Being written' : 'Replaced by a newer one') : null], pinned],
        next: {
          tip: b?.state === 'superseded' ? 'A newer brief replaced this one. Nothing is needed from you; read the newer one.' : 'Read it before what it is for. It is only a preparation: nothing is sent to anyone.',
          instead: 'If it is thin or wrong, press “Prepare me” on the meeting or conversation it is for, and your twin writes it again.',
        },
      };
    }

    case 'break':
    case 'note': {
      const label = item.label ?? (item.kind === 'break' ? 'Break' : 'Note');
      return {
        subject: { kind: item.kind === 'break' ? 'break' : 'note', title: label },
        summary: item.kind === 'break' ? `${label}: time held free in your day.` : `${label}: a note left on your day.`,
        why: placedWhy ?? 'It is on your day.',
        source: placer ? { label: `${placer.name} put it there` } : item.placedBy === 'human' ? { label: 'You put it there' } : null,
        broughtBy: placer ?? (item.placedBy === 'human' ? who.me : null),
        owner: who.me,
        involved: distinct([placer]),
        facts: [pinned],
        next: {
          tip: item.kind === 'break' ? 'Nothing is needed from you. It keeps the time free; leave it be.' : 'Nothing is needed from you. It is only a note to you.',
          instead: 'Hide it for today if you need the time or the line.',
        },
      };
    }
  }
}

/** A commitment the twin heard in a meeting's notes, before the person says whether it is theirs. */
export function explainCommitment(k: WireCommitment, twinName: string | null, meetingTitle?: string | null): ItemContext {
  const twin = twinName ?? 'Your twin';
  const text = k.text ?? 'No longer readable.';
  return {
    subject: { kind: 'commitment', title: text },
    summary: `${twin} read the notes of ${meetingTitle ? `“${meetingTitle}”` : 'this meeting'} and heard you say you would: “${text}”.`,
    need: 'Your word on whether it is yours: put it in your day as a task, or say it is not.',
    why: 'Capture was on for this meeting, so its notes were read after it ended. Nothing is in your day until you press.',
    source: { label: 'The notes of this meeting, read after it ended', when: shortDate(k.createdAt) },
    broughtBy: { name: twin, kind: 'twin', when: shortDate(k.createdAt) },
    owner: null,
    knows: [],
    facts: [['Heard', shortDate(k.createdAt)]],
    next: {
      tip: 'If it is yours, press “Put these in the day”: it becomes your own task. If someone else said it, press “Not mine” and it is dropped.',
      instead: 'If you do nothing, it stays proposed and is not in your day.',
    },
  };
}

/** A task a cover handed to the reader: whose it was meant for, and what the cover changes. */
export function explainCoveringTask(t: WireCoveringTask, who: Who): ItemContext {
  const dept = t.department ? who.label(t.department) : null;
  const gap = t.dueAt ? dayGap(t.dueAt, new Date()) : null;
  return {
    subject: { kind: 'task', title: t.title },
    summary: `A task meant for ${t.meantFor.displayName}${dept ? ` in ${dept}` : ''}. It came to you because you are covering for them.`,
    need: t.state === 'done' || t.state === 'cancelled' ? undefined : 'To be done while you cover, or left for them if it can wait.',
    why: 'A cover sends the covered person’s new tasks to the person covering, marked as theirs. Only the tasks: nothing else of theirs becomes yours.',
    source: { label: `A cover for ${t.meantFor.displayName}` },
    broughtBy: null,
    owner: who.me,
    knows: [{ name: t.meantFor.displayName, kind: 'person', role: 'it was meant for them' }],
    facts: [['Due', t.dueAt ? `${shortDate(t.dueAt)}${gap !== null ? ` (${dayWords(gap)})` : ''}` : null]],
    next: {
      tip: t.state === 'done' || t.state === 'cancelled' ? 'Nothing is needed from you; it is closed.' : `Treat it as yours until the cover ends${gap !== null && gap < 0 ? ', and it is already late, so start with it' : ''}. If it needs ${t.meantFor.displayName}’s own knowledge, ask them.`,
      instead: 'It is also on your Tasks board, where you can move it on or put time for it in your calendar.',
      actions: [{ label: 'Open the task', to: `/tasks/${t.id}` }],
    },
    open: { to: `/tasks/${t.id}`, label: 'Open the task' },
  };
}

/** A stranger who wrote in, held and unread until a person decides (ADR-0065). */
export function explainStranger(h: WireHeldSender, who: Who): ItemContext {
  const name = nameFromAddress(h.fromAddress);
  const urgent = h.unreadableInDays <= 5;
  return {
    subject: { kind: 'stranger', title: name },
    summary: `${name}${h.domain ? ` at ${h.domain}` : ''} wrote ${h.count > 1 ? `${h.count} times` : 'once'}${h.subject ? `, about “${h.subject}”` : ''}. The platform does not know them, so what they wrote is held and unread.`,
    need: 'A decision: approve them, know them without approving them, or dismiss them.',
    why: `Nobody can read it until a person decides. It has been held ${plural(h.daysHeld, 'day')} and becomes unreadable in ${plural(h.unreadableInDays, 'day')}.`,
    source: { label: 'A message from outside, held in the holding pen', to: '/strangers', when: shortDate(h.heldSince) },
    broughtBy: { name, kind: 'person', role: 'the sender', when: shortDate(h.heldSince) },
    owner: who.me,
    knows: who.knowers(h.department).slice(0, 3),
    facts: [['Held since', shortDate(h.heldSince)], ['Messages', String(h.count)]],
    next: urgent
      ? {
          tip: `Decide within ${plural(h.unreadableInDays, 'day')}: after that the message becomes unreadable and is shredded. Approve them if you work with them, or dismiss them.`,
          instead: 'Dismissing writes nothing to them; “Know them, don’t approve” files the contact without approving their company.',
        }
      : {
          tip: 'Approve them if you work with them, or dismiss them if you do not know them. “Know them, don’t approve” files the contact without approving their company.',
          instead: `If you leave it, it is shredded unread in ${plural(h.unreadableInDays, 'day')}.`,
        },
    open: { to: '/strangers', label: 'Open the holding pen' },
  };
}

/** A prospect from the morning batch: what cleared a running plan's bar, and why now. */
export function explainBatchItem(b: WireBatchItem, who: Who, department: string, placedBy: string | null): ItemContext {
  const approved = (b.company?.relationship ?? 'unapproved') !== 'unapproved';
  const name = b.businessName ?? 'A business whose name is not written down yet';
  const agent: Party | null = placedBy ? { name: `${placedBy} Agent`, kind: 'agent' } : null;
  let tip: string;
  if (approved) tip = 'Its company is already approved. Open it to see who to write to; your assistant prepares the draft and you send it.';
  else if (b.domain) tip = 'Press “Why” to read what scored. If it is a business you would work with, approve it; the platform writes to nobody either way.';
  else tip = 'It has no website address recorded yet, so it cannot be approved. Open it to see what is known, or dismiss it as not now.';
  return {
    subject: { kind: 'prospect', title: name },
    summary: `${name} in ${countryName(b.marketCode)} cleared a running plan’s bar: it scored ${b.score} against a bar of ${b.threshold}${b.opening ? ', and it has just opened' : ''}.`,
    need: approved ? 'A look, and a decision on whether to write to them.' : 'Your decision: approve the company so it can be written to, or dismiss it.',
    why: b.whyNow ?? 'A running plan swept overnight and found it above the bar.',
    source: { label: 'A prospecting plan’s overnight sweep', to: `/prospects/${b.prospectId}` },
    broughtBy: agent,
    owner: who.me,
    involved: distinct([agent]),
    knows: who.knowers(department).slice(0, 3),
    facts: [
      ['Signals', b.signals.length > 0 ? b.signals.slice(0, 3).join(' · ') : null],
      ['Company', b.company ? `${b.company.name}, ${approved ? 'approved' : 'not approved'}` : null],
    ],
    next: { tip, instead: 'Dismiss it as not now if the timing is wrong: it leaves the batch and nothing is written to them.' },
    open: { to: `/prospects/${b.prospectId}`, label: 'Open the prospect' },
  };
}
